/**
 * Reviews for one restaurant: the average, your review with edit/delete, everyone's reviews,
 * and a button to write yours. Rendered inside the expanded row and the detail sheet.
 */
import { useState } from "react";
import { Plus, Star } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/auth.provider";
import { CreateReviewRequest, ReviewWithUser, UpdateReviewRequest } from "@/types/reviews.types";

import { useRestaurantReviewManager, useRestaurantReviews } from "../hooks/use-reviews";
import { ReviewModal } from "./review-modal";
import { ReviewsList } from "./reviews-list";

interface RestaurantReviewsProps {
  restaurantId: string;
  restaurantName: string;
}

export function RestaurantReviews({ restaurantId, restaurantName }: RestaurantReviewsProps) {
  const { user } = useAuth();
  const manager = useRestaurantReviewManager(restaurantId);
  const { data: reviews = [], isLoading, error } = useRestaurantReviews(restaurantId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ReviewWithUser | null>(null);

  const stats = manager.reviewStats;
  const count = stats?.totalReviews ?? reviews.length;

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };
  const openEdit = (review: ReviewWithUser) => {
    setEditing(review);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
  };

  const submit = async (data: CreateReviewRequest | UpdateReviewRequest) => {
    if (editing || manager.hasUserReview) {
      await manager.updateReview(data as UpdateReviewRequest);
    } else {
      await manager.createReview({ ...(data as CreateReviewRequest), restaurantId });
    }
    closeModal();
  };

  return (
    <section aria-label="Reviews" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <h3 className="font-display text-base font-medium">
          Reviews{" "}
          <span className="font-sans text-[12.5px] text-muted-foreground">
            {count === 0 ? "none yet" : `${count}`}
            {stats && stats.totalReviews > 0 && (
              <>
                {" "}
                <span aria-hidden>·</span>{" "}
                <span className="inline-flex items-center gap-1 text-foreground">
                  <Star className="size-[11px] fill-star text-star-stroke" aria-hidden />
                  {stats.averageRating.toFixed(1)}
                </span>
              </>
            )}
          </span>
        </h3>
        {user && !manager.hasUserReview && (
          <Button variant="outline" size="sm" onClick={openCreate}>
            <Plus aria-hidden /> Write a review
          </Button>
        )}
      </div>

      <ReviewsList
        reviews={reviews}
        isLoading={isLoading}
        error={error?.message ?? null}
        currentUserId={user?.id}
        onEditReview={openEdit}
        onDeleteReview={() => manager.deleteReview()}
      />

      <ReviewModal
        isOpen={isModalOpen}
        onClose={closeModal}
        restaurantName={restaurantName}
        existingReview={editing ?? manager.userReview ?? null}
        onSubmit={submit}
        isLoading={manager.isMutating}
      />
    </section>
  );
}
